import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Hammer, Spinner } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { toast } from "sonner";

export default function Signup() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await signup(email, password, name);
      trackEvent("sign_up", { method: "password" });
      toast.success("Account created. Welcome.");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-zinc-950 text-zinc-100">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2.5 mb-8">
          <div className="w-9 h-9 bg-orange-600 rounded-sm flex items-center justify-center forge-pulse">
            <Hammer size={20} weight="bold" className="text-zinc-950" />
          </div>
          <div>
            <div className="font-display text-lg font-semibold">ClickForge</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-orange-400">
              Lead Agent
            </div>
          </div>
        </div>

        <div className="mb-6">
          <h2 className="font-display text-3xl font-semibold text-zinc-50">Create account</h2>
          <p className="text-sm text-zinc-500 mt-1">
            Already have one?{" "}
            <Link to="/login" data-testid="signup-go-login-link" className="text-orange-400 hover:text-orange-300 underline-offset-2 hover:underline">
              Sign in
            </Link>
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-4" data-testid="signup-form">
          <div>
            <Label htmlFor="name" className="text-xs font-mono uppercase tracking-wider text-zinc-500">Name</Label>
            <Input
              id="name"
              data-testid="signup-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="mt-1.5 bg-zinc-900 border-zinc-800 focus-visible:border-orange-500 focus-visible:ring-orange-500/30"
            />
          </div>
          <div>
            <Label htmlFor="email" className="text-xs font-mono uppercase tracking-wider text-zinc-500">Email</Label>
            <Input
              id="email"
              data-testid="signup-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="mt-1.5 bg-zinc-900 border-zinc-800 focus-visible:border-orange-500 focus-visible:ring-orange-500/30"
            />
          </div>
          <div>
            <Label htmlFor="password" className="text-xs font-mono uppercase tracking-wider text-zinc-500">Password</Label>
            <Input
              id="password"
              data-testid="signup-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              className="mt-1.5 bg-zinc-900 border-zinc-800 focus-visible:border-orange-500 focus-visible:ring-orange-500/30"
            />
          </div>

          {error ? (
            <div data-testid="signup-error" className="text-sm text-red-400 bg-red-500/5 border border-red-500/20 rounded-sm px-3 py-2">
              {error}
            </div>
          ) : null}

          <Button
            type="submit"
            data-testid="signup-submit-btn"
            disabled={loading}
            className="w-full bg-orange-600 hover:bg-orange-500 text-zinc-950 font-medium rounded-sm h-10"
          >
            {loading ? (<><Spinner size={16} className="mr-2 animate-spin" /> Creating…</>) : "Create account"}
          </Button>
        </form>
      </div>
    </div>
  );
}
